import { useEffect, useRef, useState } from 'react';
import { estimatedMinutes } from '../../content/presentation';
import { formatActiveTime } from '../../shared/date';
import { Icon } from '../../shared/Icon';
import type { AttemptInput } from '../../types/api';
import type { Exercise } from '../../types/content';
import { exerciseRegistry } from './registry';
import type { ExerciseOutcome } from './types';

interface Props {
  queue: Exercise[];
  onClose: () => void;
  onContinue: () => Promise<void>;
  onSave: (attempt: Omit<AttemptInput, 'localDate'>) => Promise<void>;
  showToast: (message: string) => void;
}

export function ExerciseSession({ queue, onClose, onContinue, onSave, showToast }: Props) {
  const [index, setIndex] = useState(0);
  const [run, setRun] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [recordedSeconds, setRecordedSeconds] = useState(0);
  const startedAt = useRef(new Date());
  const exerciseStartedAt = useRef(Date.now());
  const exercise = queue[index];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  if (!exercise) return null;
  const Plugin = exerciseRegistry[exercise.type];

  async function completeExercise(outcome: ExerciseOutcome) {
    const activeSeconds = Math.max(1, Math.min(7200, Math.round((Date.now() - exerciseStartedAt.current) / 1000)));
    try {
      await onSave({
        exerciseId: exercise.id,
        exerciseVersion: exercise.version,
        exerciseType: exercise.type,
        startedAt: startedAt.current.toISOString(),
        activeSeconds,
        response: outcome.response,
        result: outcome.result,
        evidence: outcome.evidence ?? []
      });
    } catch {
      showToast('Øvelsen ble fullført, men forsøket kunne ikke lagres.');
    }
    setRecordedSeconds(value => value + activeSeconds);
    startedAt.current = new Date();
    exerciseStartedAt.current = Date.now();
    if (outcome.repeat) return setRun(value => value + 1);
    if (index < queue.length - 1) {
      setIndex(value => value + 1);
      setRun(value => value + 1);
    } else {
      setCompleted(true);
    }
  }

  const position = Math.min(index + 1, queue.length);
  const sessionKind = queue.every(item => typeof item.content.vocabularyRank === 'number')
    ? 'vocabulary'
    : queue.every(item => typeof item.content.verbPhraseModule === 'string')
      ? 'verb-phrases'
      : 'authored';
  return <div className="modal-backdrop open" aria-hidden="false" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="practice-modal" role="dialog" aria-modal="true" aria-labelledby="modalTitle">
      <header className="modal-header">
        <button className="modal-close" aria-label="Lukk" onClick={onClose}><Icon name="close" /></button>
        <div className="modal-progress"><span>{completed ? 'ØKT FULLFØRT' : `OPPGAVE ${position} AV ${queue.length}`}</span><div><i style={{ width: `${completed ? 100 : position / queue.length * 100}%` }} /></div></div>
        <span className="modal-time">{completed ? formatActiveTime(recordedSeconds) : estimatedMinutes(exercise).toLowerCase()}</span>
      </header>
      <div className="modal-body">
        {completed ? <Completion taskCount={queue.length} seconds={recordedSeconds} sessionKind={sessionKind} onClose={onClose} onContinue={onContinue} /> : <Plugin key={`${exercise.id}:${run}`} exercise={exercise} onComplete={completeExercise} showToast={showToast} />}
      </div>
    </section>
  </div>;
}

function Completion({ taskCount, seconds, sessionKind, onClose, onContinue }: { taskCount: number; seconds: number; sessionKind: 'vocabulary' | 'verb-phrases' | 'authored'; onClose: () => void; onContinue: () => Promise<void> }) {
  const [loading, setLoading] = useState(false);
  async function continueLearning() {
    setLoading(true);
    await onContinue();
    setLoading(false);
  }
  const vocabularySession = sessionKind === 'vocabulary';
  const verbPhraseSession = sessionKind === 'verb-phrases';
  return <div className="complete-screen">
    <span className="complete-mark"><Icon name="check" /></span><p className="exercise-kicker">BRA JOBBET</p>
    <h2 id="modalTitle">{vocabularySession ? 'Denne bolken er fullført.' : verbPhraseSession ? 'Verbøkten er fullført.' : taskCount === 1 ? 'Øvelsen er fullført.' : 'Økten er fullført.'}</h2>
    <p>{vocabularySession
      ? 'Fremgangen er lagret per ord. Du kan hente neste bolk med repetisjoner og nye ord med en gang.'
      : verbPhraseSession
        ? 'Svarene er kontrollert mot de valgte betydningene. Vanskelige uttrykk kommer tilbake til repetisjon.'
        : 'Forsøket er lagret. Velg en ny øvelse når du vil trene videre.'}</p>
    <div className="complete-stats"><span><strong>{formatActiveTime(seconds)}</strong><small>målt økttid</small></span><span><strong>{taskCount}</strong><small>oppgaver</small></span><span><strong>+1</strong><small>økt fullført</small></span></div>
    <div className="completion-actions">{vocabularySession && <button className="primary-dark" disabled={loading} onClick={() => void continueLearning()}>{loading ? 'Bygger økt …' : 'Fortsett med neste bolk'} <Icon name="arrow" /></button>}<button className={vocabularySession ? 'skip-link' : 'primary-dark'} onClick={onClose}>{vocabularySession || verbPhraseSession ? 'Tilbake til oversikten' : 'Velg en ny øvelse'}</button></div>
  </div>;
}
